import { z } from 'zod';

import {
  TEMPLATE_PARENT_PARSE_ERROR,
  logCustomError,
} from '../messages/LogErrorMessages';
import { medtechApiClient } from './index';
import { MedtechCompanyUserGroup } from './user';

const TemplateParentSchema = z.object({
  id: z.number(),
  name: z.string(),
  status: z.string().nullish(),
});

export type TemplateParent = z.infer<typeof TemplateParentSchema>;

export type Template = {
  id: number;
  template_uuid: string;
  name: string;
  prefix: string;
  company_id: number;
  status: string;
  version: number;
  document_effective_on_approval: boolean;
  training_required: boolean;
  training_available: boolean;
  document_review_period: boolean;
  document_review_due_in_days: number;
  document_approval_due_in_days: number;
  document_text_sections_visible: boolean;
  file_download_user_groups: MedtechCompanyUserGroup[];
  created_time: Date;
  modified_time?: Date;
  parent?: TemplateParent | null;
};

const withValidatedParents = (templates: Template[]): Template[] =>
  templates.map((template) => {
    if (!template.parent) {
      return template;
    }
    const parsedParent = TemplateParentSchema.safeParse(template.parent);
    if (!parsedParent.success) {
      logCustomError(TEMPLATE_PARENT_PARSE_ERROR, {
        error: parsedParent.error,
        companyId: template.company_id,
        entityId: template.id,
      });
      return { ...template, parent: null };
    }
    return { ...template, parent: parsedParent.data };
  });

const templateApi = {
  async getTemplates(companyId: number): Promise<Template[]> {
    const res = await medtechApiClient.get(
      `/${companyId}/document_templates?statuses=effective`,
    );
    return withValidatedParents(res.data as Template[]);
  },
  async getTemplate(companyId: number, templateId: number): Promise<Template> {
    const res = await medtechApiClient.get(
      `/${companyId}/document_templates/${templateId}`,
    );
    const [template] = withValidatedParents([res.data as Template]);
    return template;
  },
  async getArchivedTemplates(companyId: number): Promise<Template[]> {
    const res = await medtechApiClient.get(
      `/${companyId}/document_templates?statuses=archived`,
    );
    return withValidatedParents(res.data as Template[]);
  },
  async getAllTemplatesV2(companyId: number): Promise<Template[]> {
    const effectiveTemplatesPromise = await this.getTemplates(companyId);
    const archivedTemplatesPromise = await this.getArchivedTemplates(companyId);
    const [effectiveTemplates, archivedTemplates] = await Promise.all([
      effectiveTemplatesPromise,
      archivedTemplatesPromise,
    ]);
    return [...effectiveTemplates, ...archivedTemplates];
  },
};

export default templateApi;
